import { useClient } from "@/composables/useClient";
import { deserializeCbiDto, serializeCbiDto, encodeRequestParam } from "../serialization/CbiDateCodec";
import type { MemberApi } from "../../api/MemberApi";
import type { AuthTokens } from "../../generated/dto/AuthTokens";
import type { CompleteMemberProfile } from "../../generated/dto/CompleteMemberProfile";
import type { CompleteMemberRegistration } from "../../generated/dto/CompleteMemberRegistration";
import type { MemberAgreementStatus } from "../../generated/dto/MemberAgreementStatus";
import type { MemberAgreementStatuses } from "../../generated/dto/MemberAgreementStatuses";
import type { MemberRequiredAgreementsRequest } from "../../generated/dto/MemberRequiredAgreementsRequest";
import type { MemberSignAgreementRequest } from "../../generated/dto/MemberSignAgreementRequest";
import type { ProtoLoginResult } from "../../generated/dto/ProtoLoginResult";
import type { RecipientLookupRequest } from "../../generated/dto/RecipientLookupRequest";
import type { RecipientLookupResult } from "../../generated/dto/RecipientLookupResult";

export class MemberApiClient implements MemberApi {
  constructor(private readonly client = useClient()) {}

  async completeProfileMember(request: CompleteMemberProfile): Promise<AuthTokens> {
    const { data } = await this.client.post<unknown>(`/member/member/completeprofile`, serializeCbiDto(request, "generated.dto.CompleteMemberProfile"));
    return deserializeCbiDto<AuthTokens>(data, "generated.dto.AuthTokens");
  }

  async completeRegistrationMember(request: CompleteMemberRegistration): Promise<AuthTokens> {
    const { data } = await this.client.post<unknown>(`/member/member/completeregistration`, serializeCbiDto(request, "generated.dto.CompleteMemberRegistration"));
    return deserializeCbiDto<AuthTokens>(data, "generated.dto.AuthTokens");
  }

  async createMemberPublic(email: string): Promise<AuthTokens> {
    const { data } = await this.client.post<unknown>(`/member/public/createmember`, JSON.stringify(email));
    return deserializeCbiDto<AuthTokens>(data, "generated.dto.AuthTokens");
  }

  async createMemberFromProtoMember(email: string): Promise<ProtoLoginResult> {
    const { data } = await this.client.post<unknown>(`/member/member/creatememberfromproto`, JSON.stringify(email));
    return deserializeCbiDto<ProtoLoginResult>(data, "generated.dto.ProtoLoginResult");
  }

  async lookupRecipientMember(request: RecipientLookupRequest): Promise<RecipientLookupResult> {
    const { data } = await this.client.post<unknown>(`/member/member/lookuprecipient`, serializeCbiDto(request, "generated.dto.RecipientLookupRequest"));
    return deserializeCbiDto<RecipientLookupResult>(data, "generated.dto.RecipientLookupResult");
  }

  async requiredAgreementsMember(request: MemberRequiredAgreementsRequest): Promise<MemberAgreementStatuses> {
    const { data } = await this.client.post<unknown>(`/member/member/requiredagreements`, serializeCbiDto(request, "generated.dto.MemberRequiredAgreementsRequest"));
    return deserializeCbiDto<MemberAgreementStatuses>(data, "generated.dto.MemberAgreementStatuses");
  }

  async signAgreementMember(request: MemberSignAgreementRequest): Promise<MemberAgreementStatus> {
    const { data } = await this.client.post<unknown>(`/member/member/signagreement`, serializeCbiDto(request, "generated.dto.MemberSignAgreementRequest"));
    return deserializeCbiDto<MemberAgreementStatus>(data, "generated.dto.MemberAgreementStatus");
  }
}

export const useMemberApi = (): MemberApi => new MemberApiClient();
